import { useState, useEffect } from 'react';
import { Link } from 'react-admin';
import { Box, Typography, Card, CardContent, Chip } from '@mui/material';
import { Phone, PhoneIncoming, PhoneOutgoing } from 'lucide-react';
import { useActiveCalls, ActiveCall } from '../hooks/useActiveCalls';

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function LiveTimer({ startedAt }: { startedAt: string | null }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!startedAt) return;
    const start = new Date(startedAt).getTime();

    const tick = () => setElapsed(Math.floor((Date.now() - start) / 1000));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  if (!startedAt) return null;
  return <span>{formatDuration(elapsed)}</span>;
}

function CallEntry({ activeCall }: { activeCall: ActiveCall }) {
  const { call, funerals, lead } = activeCall;
  const isInbound = call.direction === 'inbound';
  const customerPhone = isInbound ? call.from_number : call.to_number;
  const DirectionIcon = isInbound ? PhoneIncoming : PhoneOutgoing;

  return (
    <Box sx={{ mb: 1, '&:last-child': { mb: 0 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <DirectionIcon size={14} />
        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
          {customerPhone || 'Unknown'}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
          <LiveTimer startedAt={call.started_at} />
        </Typography>
      </Box>

      {call.agent_name && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', ml: 2.5 }}>
          {call.agent_name}
        </Typography>
      )}

      {funerals.length > 0 ? (
        <Box sx={{ ml: 2.5, mt: 0.5 }}>
          {funerals.map((f) => (
            <Link
              key={f.funeral_id}
              to={`/funerals/${f.funeral_id}/show`}
              style={{ textDecoration: 'none', display: 'block' }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'primary.main',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                #{f.service_id} — {f.deceased_name || 'N/A'}
              </Typography>
            </Link>
          ))}
        </Box>
      ) : lead ? (
        <Box sx={{ ml: 2.5, mt: 0.5 }}>
          <Link
            to={`/leads/${lead.lead_id}/show`}
            style={{ textDecoration: 'none', display: 'block' }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'primary.main',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {lead.contact_name || 'Lead'}{lead.intent ? ` — ${lead.intent}` : ''}{lead.stage ? ` (${lead.stage})` : ''}
            </Typography>
          </Link>
        </Box>
      ) : (
        <Typography variant="caption" sx={{ color: 'text.secondary', ml: 2.5, display: 'block', mt: 0.5 }}>
          Unknown caller
        </Typography>
      )}
    </Box>
  );
}

export function ActiveCallCard() {
  const activeCalls = useActiveCalls();

  if (activeCalls.length === 0) return null;

  return (
    <Card
      sx={{
        mx: 1,
        my: 1,
        border: '1px solid',
        borderColor: 'success.light',
        bgcolor: 'success.50',
      }}
      variant="outlined"
    >
      <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'success.main',
              animation: 'pulse 1.5s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.4 },
              },
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Phone size={14} />
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Active Call{activeCalls.length > 1 ? 's' : ''}
            </Typography>
          </Box>
          {activeCalls.length > 1 && (
            <Chip label={activeCalls.length} size="small" color="success" sx={{ height: 18, fontSize: 11 }} />
          )}
        </Box>

        {activeCalls.map((ac) => (
          <CallEntry key={ac.call.id} activeCall={ac} />
        ))}
      </CardContent>
    </Card>
  );
}
